/**
 * contextFields — les liaisons d'un MODÈLE au contenu qu'il affiche.
 *
 * Une page-modèle (`modelFor`) rend une instance d'objet : ses blocs lisent
 * `~~._context_.<clé>`, que `BuilderBlockMount` injecte dans leur store. Ce
 * module porte la seule chose que cette liaison ne dit pas d'elle-même : à
 * QUEL champ de l'objet elle correspond, et donc si elle est modifiable.
 *
 * Rien n'est déclaré nulle part : `content: ~~._context_.title` EST le contrat
 * d'éditabilité — exactement comme `inputAttributes.placeholder` l'est pour le
 * texte indicatif d'un formulaire (cf. `placeholderFieldOf` dans
 * `expandEditable`). `expandEditable` estampille le chemin
 * (`data-builder-context-field`), et c'est ICI qu'on décide, au moment de
 * l'édition, s'il tombe sur un champ réel du type d'objet courant.
 *
 * L'éditeur (`text`, `rich-text`, `image-drupal`…) vient du `fields[]` du TYPE
 * D'OBJET, jamais du bloc : c'est ce qui laisse deux types différents partager
 * le même bloc de modèle, chacun avec son propre mapping.
 */

/** Préfixe d'un chemin reactive-json pointant l'objet courant. */
export const CONTEXT_PREFIX = '~~._context_.'

/**
 * Le chemin d'objet porté par une valeur de rjBuild, ou null.
 * `'~~._context_.cover.alt'` → `'cover.alt'`.
 */
export const contextPathOf = (value) => {
  if (typeof value !== 'string' || !value.startsWith(CONTEXT_PREFIX)) return null
  const path = value.slice(CONTEXT_PREFIX.length).trim()
  return /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/.test(path) ? path : null
}

/**
 * Sous-chemins tolérés APRÈS la clé du champ. Un chemin plus profond n'est
 * pas une autre donnée : c'est une facette de la MÊME valeur, que l'éditeur du
 * champ gouverne en entier.
 *
 *  - `rich-text`    → `.processed` est le rendu serveur ; on édite `{value, format}`
 *  - `image-drupal` → `.url` / `.alt` / `.src` appartiennent à l'image
 *
 * Tout le reste (`category.name` d'un `object-ref` : un AUTRE objet) reste en
 * lecture — on n'édite pas un contenu voisin depuis la fiche courante.
 */
const SUBPATHS = {
  'rich-text': ['processed'],
  'image-drupal': ['url', 'alt', 'src'],
  image: ['url'],
}

/** Le mapping `{cléDeContexte: cléDObjet}` inversé, ou null si passthrough. */
const isMapped = (mapping) => mapping && typeof mapping === 'object' && Object.keys(mapping).length > 0

/**
 * Applique un `mapping: {contextKey: objectKey}` à la donnée d'une instance et
 * renvoie le dict à injecter sous `_context_`. Passthrough si le mapping est
 * absent. Partagé par le rendu public et l'édition d'objet dans un modèle —
 * les deux doivent voir EXACTEMENT le même contexte.
 */
export const applyContextMapping = (data, mapping) => {
  const source = data ?? {}
  if (!isMapped(mapping)) return source
  const out = {}
  for (const [contextKey, objectKey] of Object.entries(mapping)) {
    out[contextKey] = source[objectKey]
  }
  return out
}

/**
 * Résout un chemin de contexte contre le schéma d'un type d'objet.
 *
 * @param {string} path      Chemin sans le préfixe (`title`, `cover.alt`).
 * @param {object} spec      `{ fields: [...], mapping: {contextKey: objectKey} }`
 * @returns {{contextKey: string, objectKey: string, field: object}|null}
 *          null = ce point du modèle n'est lié à aucun champ éditable du type,
 *          donc il reste en lecture (c'est la règle demandée : seuls les
 *          éléments qui MAPPENT un champ sont modifiables).
 */
export const resolveContextField = (path, { fields = [], mapping = null } = {}) => {
  if (typeof path !== 'string' || path === '') return null
  const [contextKey, ...rest] = path.split('.')

  // Un mapping explicite RESTREINT la surface : `buildContext` ne publie que
  // ses clés, donc une clé hors mapping ne désigne aucune donnée.
  let objectKey = contextKey
  if (isMapped(mapping)) {
    if (!(contextKey in mapping)) return null
    objectKey = mapping[contextKey]
  }

  const field = fields.find((f) => f && f.key === objectKey)
  if (!field) return null

  const type = field.type ?? field.editorType
  if (!type) return null

  if (rest.length > 0) {
    const allowed = SUBPATHS[type] ?? []
    if (rest.length > 1 || !allowed.includes(rest[0])) return null
  }

  return { contextKey, objectKey, field }
}

export default resolveContextField
